/**
 * ListCard is a flexible, full-width card component for displaying items in lists.
 * Supports status variants (default, warning, error, success), click handling, and disabled state.
 * Designed for use in Block Kit Style Cards (see DOCUMENTATION.md) to build interactive, consistent UIs.
 */
import React from 'react';
import { Card, cn } from '@strivelabs/ui-core';

export interface ListCardProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  variant?: 'default' | 'warning' | 'error' | 'success';
}

const variantClasses = {
  default: 'hover:bg-muted/50 border-border',
  warning: 'border-yellow-200 bg-yellow-50/50 hover:bg-yellow-100/50',
  error: 'border-red-200 bg-red-50/50 hover:bg-red-100/50',
  success: 'border-green-200 bg-green-50/50 hover:bg-green-100/50'
};

export const ListCard: React.FC<ListCardProps> = ({
  children,
  onClick,
  className,
  disabled = false,
  variant = 'default'
}) => {
  const isClickable = onClick && !disabled;

  return (
    <Card
      className={cn(
        'w-full p-4 transition-colors',
        variantClasses[variant],
        isClickable && 'cursor-pointer hover:shadow-md',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onClick={isClickable ? onClick : undefined}
    >
      <div className="flex items-center justify-between w-full">
        {children}
      </div>
    </Card>
  );
};